import apiService from './api';

/**
 * Preferências de notificação por empresa: quais perfis veem cada aviso dentro
 * do sistema e para quais e-mails ele também sai.
 *
 * Espelha `notificacoes_preferencias` no servidor (backend/database/migrations
 * — ver comentário lá para o porquê de não ter exclusão lógica: a ausência de
 * configuração salva SIGNIFICA "usa o padrão do catálogo", e não "não recebe
 * nada"). O mesmo tipo de aviso pode não ter linha salva (`personalizado:false`,
 * valores vindos de `config/notificacoes.php`) ou ter sido customizado pela
 * empresa (`personalizado:true`).
 */

/**
 * Perfis fixos do sistema. Motorista é incluído porque também recebe avisos
 * (ex.: "sua CNH está vencendo"), mesmo não tendo acesso ao painel administrativo.
 */
export type PerfilChave = 'admin' | 'gestor' | 'operador' | 'motorista';

export interface EmailDestinatario {
  email: string;
  nome: string;
}

/**
 * Sugestão de e-mail para preencher o campo de destinatários (ex.: e-mail de
 * um usuário já cadastrado na empresa). `role` é o perfil dessa pessoa, para a
 * interface poder agrupar ou rotular a sugestão.
 */
export interface SugestaoEmail extends EmailDestinatario {
  role: PerfilChave;
}

export interface TipoPreferencia {
  tipo: string;
  rotulo: string;
  descricao: string;
  /** Módulo cujo `consultar` controla quem pode ver este aviso (ex.: "aprovacoes"). */
  modulo: string;
  /** Rótulo do módulo pronto para exibição (ex.: "Aprovação de motoristas"). */
  modulo_rotulo: string;
  /** Se a empresa já salvou uma configuração própria para este tipo. */
  personalizado: boolean;
  atualizado_em: string | null;
  perfis: Record<PerfilChave, boolean>;
  /**
   * Perfis que não podem ser marcados porque o perfil não tem `consultar` no
   * módulo de origem do aviso — travados na grade, e não apenas desmarcados,
   * para não sugerir que dá para religar por aqui.
   */
  perfis_bloqueados: PerfilChave[];
  email_ativo: boolean;
  emails: EmailDestinatario[];
  /** Se a lista de e-mails ainda é a padrão do sistema (nenhuma customizada). */
  usando_padrao_de_emails: boolean;
}

export interface PreferenciasData {
  /** Rótulo de exibição de cada perfil (ex.: admin -> "Administrador"). */
  perfis: Record<PerfilChave, string>;
  /** Máximo de e-mails destinatários aceitos por tipo de aviso. */
  limite_emails: number;
  tipos: TipoPreferencia[];
  sugestoes_de_email: SugestaoEmail[];
}

export interface PreferenciasResponse {
  success: boolean;
  data: PreferenciasData;
}

/**
 * Recorte de `TipoPreferencia` que a tela edita e envia de volta ao salvar.
 * Os campos derivados pelo servidor (rótulo, descrição, bloqueios, etc.) não
 * fazem parte do corpo da requisição — só o que a pessoa pode alterar.
 */
export interface TipoPreferenciaEdicao {
  tipo: string;
  perfis: Record<PerfilChave, boolean>;
  email_ativo: boolean;
  emails: EmailDestinatario[];
}

export interface SalvarPreferenciasBody {
  tipos: TipoPreferenciaEdicao[];
}

export interface SalvarPreferenciasResponse {
  success: boolean;
  message: string;
  data: {
    tipos: TipoPreferencia[];
    alteracoes: number;
  };
}

export interface RestaurarPreferenciaResponse {
  success: boolean;
  message: string;
  /** O tipo já recalculado com `personalizado:false` e os valores do padrão. */
  data: TipoPreferencia;
}

export interface EnviarTesteResponse {
  success: boolean;
  message: string;
}

export const notificacoesConfigService = {
  buscar: async () =>
    apiService.get<PreferenciasResponse>('/v1/preferencias-notificacoes'),

  salvar: async (body: SalvarPreferenciasBody) =>
    apiService.put<SalvarPreferenciasResponse>('/v1/preferencias-notificacoes', body),

  restaurar: async (tipo: string) =>
    apiService.delete<RestaurarPreferenciaResponse>(`/v1/preferencias-notificacoes/${tipo}`),

  enviarTeste: async (tipo: string) =>
    apiService.post<EnviarTesteResponse>(`/v1/preferencias-notificacoes/${tipo}/teste`),
};
